/**
 * @file Canonical responsive JS hooks. See src/theme/RESPONSIVE.md.
 *
 * JS hooks are the LAST resort — prefer responsive style props
 * (`span={{ base: 12, sm: 6 }}`), hiddenFrom/visibleFrom, or CSS modules
 * with $mantine-breakpoint-* vars. Reach for these only to swap render
 * trees or drive props that cannot be responsive (Modal fullScreen,
 * DataGrid column models).
 */
import { useMediaQuery } from '@mantine/hooks';
import { useMatches } from '@mantine/core';
import { breakpoints } from '../theme/breakpoints';

// theme/breakpoints.js is the single source of truth ('37.5em' → 37.5).
const em = (key) => parseFloat(breakpoints[key]);
// Epsilon for exclusive max-width bounds (1px at 16px root).
const EPS = 0.0625;

/**
 * True below the `sm` breakpoint (phones). Use to swap render trees or set
 * non-responsive props; never to duplicate what a style prop can do.
 * `getInitialValueInEffect: false` reads the media query synchronously so
 * mobile does not flash the desktop tree on first paint.
 */
export function useIsMobile() {
  return useMediaQuery(`(max-width: ${em('sm') - EPS}em)`, false, { getInitialValueInEffect: false });
}

/**
 * Mantine's per-breakpoint value selector (mobile-first), re-exported so
 * app code imports every responsive hook from this file.
 * Example: const cols = useMatches({ base: 1, sm: 2, lg: 4 });
 */
export { useMatches };

// ----------------------------------------------------------------------
// Deprecated MUI-era API kept for existing call sites. Do not add usages.
// ----------------------------------------------------------------------

/** @deprecated Use useIsMobile(), useMatches(), or responsive style props. */
export function useResponsive(query, start, end) {
  let mediaQuery;

  if (query === 'up') {
    mediaQuery = `(min-width: ${em(start)}em)`;
  } else if (query === 'down') {
    mediaQuery = `(max-width: ${em(start) - EPS}em)`;
  } else if (query === 'between') {
    mediaQuery = `(min-width: ${em(start)}em) and (max-width: ${em(end) - EPS}em)`;
  } else {
    // 'only'
    const keys = Object.keys(breakpoints);
    const nextKey = keys[keys.indexOf(start) + 1];
    mediaQuery = nextKey ? `(min-width: ${em(start)}em) and (max-width: ${em(nextKey) - EPS}em)` : `(min-width: ${em(start)}em)`;
  }

  return useMediaQuery(mediaQuery, false, { getInitialValueInEffect: false });
}

/** @deprecated Use useMatches() — this burns four media-query listeners per call. */
export function useWidth() {
  const matchXl = useMediaQuery(`(min-width: ${em('xl')}em)`);
  const matchLg = useMediaQuery(`(min-width: ${em('lg')}em)`);
  const matchMd = useMediaQuery(`(min-width: ${em('md')}em)`);
  const matchSm = useMediaQuery(`(min-width: ${em('sm')}em)`);

  if (matchXl) return 'xl';
  if (matchLg) return 'lg';
  if (matchMd) return 'md';
  if (matchSm) return 'sm';
  return 'xs';
}
